import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { Box, Snackbar, Typography } from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import { ToastContext, type ToastType } from '@/shared/contexts/ToastContext'

// 旧 kakeru UI 用 Toast Provider. legacy 階層内で完結.
//   - filled MuiAlert (= severity 別 bg + white text + white icon)
//   - 色は旧 theme.palette.toast.* 由来 (= SoapLoadingToast と同色系)
//   - position は bottom-center 固定
// Notion 黒トースト spec を採らない理由: legacy app の見た目を保ち latest との
// 視覚的混在を避けるため. shared/styles 非依存 (= legacy app 削除時に本 file ごと消える).
const ALERT_SX: Record<ToastType, { backgroundColor: string; border: string }> =
  {
    success: {
      backgroundColor: 'rgba(76, 175, 80, 0.85)',
      border: '1px solid rgba(76, 175, 80, 0.5)',
    },
    error: {
      backgroundColor: 'rgba(208, 0, 0, 0.85)',
      border: '1px solid rgba(208, 0, 0, 0.5)',
    },
    warning: {
      backgroundColor: 'rgba(208, 0, 0, 0.85)',
      border: '1px solid rgba(208, 0, 0, 0.5)',
    },
    info: {
      backgroundColor: 'rgba(97, 97, 97, 0.9)',
      border: '1px solid rgba(97, 97, 97, 0.6)',
    },
  }

const MESSAGE_SX = { color: 'white', fontWeight: 500 }

export const LegacyToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [type, setType] = useState<ToastType>('info')
  const [duration, setDuration] = useState(3000)
  // open=true 中の連続 showToast race ガード. 100ms 内の追加呼出しを drop する.
  const [isProcessing, setIsProcessing] = useState(false)

  const showToast = useCallback(
    (
      newMessage: string,
      newType: ToastType = 'info',
      autoHideDuration = 3000
    ) => {
      if (isProcessing) return

      const applyNext = () => {
        setMessage(newMessage)
        setType(newType)
        setDuration(autoHideDuration)
        setOpen(true)
      }

      if (open) {
        setIsProcessing(true)
        setOpen(false)
        window.setTimeout(() => {
          applyNext()
          setIsProcessing(false)
        }, 100)
      } else {
        applyNext()
      }
    },
    [open, isProcessing]
  )

  const hideToast = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!open || duration <= 0) return
    const id = window.setTimeout(() => hideToast(), duration)
    return () => window.clearTimeout(id)
  }, [open, duration, hideToast])

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={open}
        autoHideDuration={duration}
        onClose={hideToast}
      >
        {/* Box でラップ: Snackbar の Grow が direction prop を渡すため (MUI Issue #35649) */}
        <Box>
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={type}
            onClose={hideToast}
            sx={{
              ...ALERT_SX[type],
              '& .MuiAlert-icon': { color: 'white' },
            }}
          >
            <Typography sx={MESSAGE_SX}>{message}</Typography>
          </MuiAlert>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  )
}
